import React from "react";
import { useParams, Link } from "react-router-dom";
import { blogPosts } from "../data/blogPosts";
import SEO from "../components/SEO";
import { generateBreadcrumbSchema } from "../utils/schemaMarkup";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BlogPost = () => {
  const { slug } = useParams();

  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="page active">
        <h2>Blog Post Not Found</h2>
        <p>The blog post you're looking for doesn't exist.</p>
        <Link to="/blog">Back to Blog</Link>
      </div>
    );
  }

  const postUrl = `https://www.ceekayenterprise.com/blog/${post.slug}`;

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://www.ceekayenterprise.com/" },
    { name: "Blog", url: "https://www.ceekayenterprise.com/blog" },
    { name: post.title, url: postUrl },
  ]);

  // Article schema for rich snippets
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.image ? `https://www.ceekayenterprise.com${post.image}` : undefined,
    author: {
      "@type": "Organization",
      name: post.author,
    },
    datePublished: post.date,
    dateModified: post.date,
    publisher: {
      "@type": "Organization",
      name: "Ceekay Enterprise",
      logo: {
        "@type": "ImageObject",
        url: "https://www.ceekayenterprise.com/ceekay-logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": postUrl,
    },
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [articleSchema, breadcrumbSchema],
  };

  return (
    <div className="page active">
      <SEO
        title={`${post.title} | Ceekay Enterprise Blog`}
        description={post.excerpt}
        keywords={post.keywords}
        canonical={postUrl}
        ogImage={post.image ? `https://www.ceekayenterprise.com${post.image}` : undefined}
        ogType="article"
        schema={combinedSchema}
      />

      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span> / </span>
        <Link to="/blog">Blog</Link>
        <span> / </span>
        <span>{post.title}</span>
      </nav>

      <article className="blog-post">
        <div className="blog-post-header">
          <div className="blog-post-meta">
            <span className="blog-category">{post.category}</span>
            <span className="blog-date">
              {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="blog-author">By {post.author}</span>
          </div>
          <h1 className="blog-post-title">{post.title}</h1>
          {post.image && (
            <div className="blog-post-image">
              <img src={post.image} alt={post.title} loading="lazy" />
            </div>
          )}
        </div>

        <div className="blog-post-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <footer className="blog-post-footer">
          <div className="blog-post-tags">
            <h3>Tags:</h3>
            {post.tags.map((tag, index) => (
              <span key={index} className="blog-tag">{tag}</span>
            ))}
          </div>
        </footer>
      </article>

      {/* Related Posts */}
      <div className="related-posts">
        <h2>Related Articles</h2>
        <div className="related-posts-grid">
          {blogPosts
            .filter((p) => p.id !== post.id && p.category === post.category)
            .slice(0, 2)
            .map((relatedPost) => (
              <Link
                key={relatedPost.id}
                to={`/blog/${relatedPost.slug}`}
                className="related-post-card"
              >
                {relatedPost.image && (
                  <img src={relatedPost.image} alt={relatedPost.title} />
                )}
                <h4>{relatedPost.title}</h4>
                <p>{relatedPost.excerpt.substring(0, 100)}...</p>
              </Link>
            ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="blog-post-cta">
        <h3>Interested in Our Products?</h3>
        <p>Explore our comprehensive range of industrial chemicals and request a quote today.</p>
        <div className="blog-post-cta-buttons">
          <Link to="/products" className="primary-cta">
            View Products
          </Link>
          <Link to="/contact" className="secondary-cta">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;

